import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import userEvent from "@testing-library/user-event";
import "@testing-library/jest-dom";
import { MemoryRouter, Route, Routes } from "react-router-dom";

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));

const mockCreateConfig = jest.fn().mockResolvedValue({ id: "config-1" });
jest.mock("../hooks", () => ({
  useDigestConfigGet: () => jest.fn(),
  useDigestConfigCreate: () => mockCreateConfig,
  useDigestConfigUpdate: () => jest.fn(),
}));

const mockReadUser = jest.fn();
jest.mock("../services/api", () => ({
  useReadUser: () => mockReadUser,
}));

jest.mock("../components/BKGAgent", () => ({
  FloatingChatWidget: () => <div data-testid="floating-chat-widget" />,
}));

const DigestConfigForm = require("./DigestConfigForm").default;

const renderForm = (search = "") =>
  render(
    <MemoryRouter
      initialEntries={[
        `/workspace/workspace-1/bkg/catalyst-events/digests/new${search}`,
      ]}
    >
      <Routes>
        <Route
          path="/workspace/:workspace_id/bkg/catalyst-events/digests/new"
          element={<DigestConfigForm />}
        />
        <Route
          path="/workspace/:workspace_id/bkg/catalyst-events/digests"
          element={<div data-testid="digest-list" />}
        />
      </Routes>
    </MemoryRouter>,
  );

describe("DigestConfigForm templates", () => {
  beforeEach(() => {
    mockCreateConfig.mockClear();
    mockReadUser.mockReset();
    mockReadUser.mockResolvedValue({ id: "user-1", email: "kim@example.com" });
  });

  it("starts empty and manual without a template", () => {
    renderForm();

    expect(
      screen.getByPlaceholderText("e.g. Weekly Phase 3 Readouts"),
    ).toHaveValue("");
    expect(screen.queryByText(/Started from the/)).toBeNull();
    expect(mockReadUser).not.toHaveBeenCalled();
  });

  it("prefills the form from a template and saves it as a weekly emailed digest", async () => {
    renderForm("?template=weekly-biotech-intelligence");

    expect(
      screen.getByPlaceholderText("e.g. Weekly Phase 3 Readouts"),
    ).toHaveValue("Weekly Biotech Intelligence");
    expect(screen.getByText(/Started from the/)).toBeInTheDocument();
    expect(
      await screen.findByDisplayValue("kim@example.com"),
    ).toBeInTheDocument();

    await userEvent.click(
      screen.getByRole("button", { name: "Create Digest" }),
    );

    await waitFor(() => expect(mockCreateConfig).toHaveBeenCalledTimes(1));
    const [workspaceId, payload] = mockCreateConfig.mock.calls[0];
    expect(workspaceId).toBe("workspace-1");
    expect(payload).toMatchObject({
      name: "Weekly Biotech Intelligence",
      interval: "weekly",
      email_enabled: true,
      email_recipients: [{ type: "email", address: "kim@example.com" }],
    });
    expect(payload.prioritisation_prompt).toContain("fundraising");
    expect(payload.event_type_filters).toContain("Funding Round");
    expect(payload.anchor_time).toMatch(/^\d{2}:\d{2}$/);
    expect(payload.anchor_day).not.toBeNull();
  });

  it("keeps Create Digest disabled while the creator's email is loading", async () => {
    let resolveUser: (user: { id: string; email: string }) => void = () => {};
    mockReadUser.mockReturnValue(
      new Promise((resolve) => {
        resolveUser = resolve;
      }),
    );
    renderForm("?template=weekly-biotech-intelligence");

    const createButton = screen.getByRole("button", { name: "Create Digest" });
    expect(createButton).toBeDisabled();

    resolveUser({ id: "user-1", email: "kim@example.com" });
    await waitFor(() => expect(createButton).toBeEnabled());
    expect(screen.getByDisplayValue("kim@example.com")).toBeInTheDocument();
  });

  it("surfaces a failed email lookup and refuses to save without recipients", async () => {
    jest.spyOn(console, "error").mockImplementation(() => {});
    mockReadUser.mockRejectedValue(new Error("network down"));
    renderForm("?template=weekly-biotech-intelligence");

    expect(
      await screen.findByText(/look up your email address/),
    ).toBeInTheDocument();

    await userEvent.click(
      screen.getByRole("button", { name: "Create Digest" }),
    );

    expect(
      screen.getByText(
        "Add at least one email recipient, or turn off email delivery.",
      ),
    ).toBeInTheDocument();
    expect(mockCreateConfig).not.toHaveBeenCalled();
    (console.error as jest.Mock).mockRestore();
  });

  it("ignores an unknown template id", () => {
    renderForm("?template=nope");

    expect(
      screen.getByPlaceholderText("e.g. Weekly Phase 3 Readouts"),
    ).toHaveValue("");
    expect(mockReadUser).not.toHaveBeenCalled();
  });
});
